import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { AccountDialog } from "@/components/AccountDialog";
import { LayoutDashboard, Users, CalendarDays, Send, ClipboardList, ShieldCheck, LogOut, Egg, Menu, UserCog, BookOpen } from "lucide-react";

const BASE_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/staff", label: "Staff", icon: Users, id: "staff" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, id: "schedule" },
  { to: "/send", label: "Send Alerts", icon: Send, id: "send" },
  { to: "/summary", label: "Delivery Summary", icon: ClipboardList, id: "summary" },
  { to: "/guide", label: "Guide", icon: BookOpen, id: "guide" },
];

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <span className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
        <Egg className="h-5 w-5" />
      </span>
      <div className="leading-none">
        <p className="font-head font-700 tracking-tight text-sm">Montek Co &amp; PL</p>
        <p className="text-xs text-muted-foreground">Shift Alerts</p>
      </div>
    </div>
  );
}

function NavItems({ items, onNavigate }) {
  return (
    <nav className="flex-1 p-3 space-y-1">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          data-testid={`nav-${n.id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`
          }
        >
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  const items = user?.role === "admin"
    ? [...BASE_NAV, { to: "/admin", label: "Admin", icon: ShieldCheck, id: "admin" }]
    : BASE_NAV;

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  const openAccount = () => { setOpen(false); setAccountOpen(true); };

  const Footer = () => (
    <div className="p-3 border-t border-border space-y-1">
      <p className="px-3 text-xs text-muted-foreground truncate" data-testid="current-user-email">
        {user?.email}{user?.role === "admin" ? " · Admin" : ""}
      </p>
      <Button variant="ghost" onClick={openAccount} data-testid="account-button"
        className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground">
        <UserCog className="h-4 w-4" /> Account
      </Button>
      <Button variant="ghost" onClick={handleLogout} data-testid="logout-button"
        className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive">
        <LogOut className="h-4 w-4" /> Sign out
      </Button>
      <Link to="/terms" onClick={() => setOpen(false)} data-testid="nav-terms" className="block px-3 pt-1 text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline">
        Terms &amp; Conditions
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[240px] bg-card border-r border-border flex-col fixed inset-y-0 left-0 z-30">
        <div className="p-5 border-b border-border"><Brand /></div>
        <NavItems items={items} />
        <Footer />
      </aside>

      {/* Mobile / tablet top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-card border-b border-border px-4 h-16">
        <Brand />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[260px] flex flex-col">
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
            <div className="p-5 border-b border-border"><Brand /></div>
            <NavItems items={items} onNavigate={() => setOpen(false)} />
            <Footer />
          </SheetContent>
        </Sheet>
      </header>

      <main className="lg:ml-[240px] p-4 sm:p-6 lg:p-8 max-w-[1400px]">
        <Outlet />
      </main>

      <AccountDialog open={accountOpen} onOpenChange={setAccountOpen} />
    </div>
  );
}
